import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  QrCode, Download, Loader2, ShieldCheck, Gift, Share2, Copy, CheckCircle2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TesseraReferralSection({ user }) {
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/me/referral`, { withCredentials: true })
      .then((r) => setRef(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const downloadTessera = () => window.open(`${API}/api/me/tessera/pdf`, "_blank");
  const qrUrl = `${API}/api/me/tessera/qr.png`;

  const copyLink = () => {
    if (!ref?.ref_link) return;
    navigator.clipboard.writeText(ref.ref_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhats = () => {
    if (!ref?.ref_link) return;
    const msg = `Ciao! Ti invito a iscriverti ad A.C.D.T., la mia associazione di Consulenti Digitali. Usa il mio codice: ${ref.ref_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div data-testid="tessera-referral" className="grid md:grid-cols-2 gap-4">
      {/* Tessera digitale */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-[#102a6b] text-white flex items-center justify-center">
            <QrCode className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#102a6b]">Tessera Digitale</div>
            <h3 className="font-extrabold text-[#0a1f55]">La tua tessera QR</h3>
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 p-4 flex items-center gap-4">
          <img src={qrUrl} alt="QR Tessera" className="h-24 w-24 border-2 border-white shadow rounded-lg bg-white" />
          <div className="flex-1 min-w-0">
            <div className="text-[11.5px] uppercase tracking-wider font-extrabold text-slate-500">N° Tessera</div>
            <div className="font-extrabold text-[#0a1f55] font-mono text-[14px] break-all">{user?.numero_tessera || "-"}</div>
            <div className="text-[11.5px] text-slate-500 mt-1">Scansiona per verificare l&apos;autenticità</div>
          </div>
        </div>
        <button onClick={downloadTessera} data-testid="download-tessera-pdf"
          className="mt-4 w-full h-10 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold text-[13px] inline-flex items-center justify-center gap-2">
          <Download className="h-4 w-4" /> Scarica tessera PDF stampabile
        </button>
      </div>

      {/* Referral */}
      <div className="rounded-2xl border-2 border-[#f59e0b]/40 bg-gradient-to-br from-amber-50 to-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-[#f59e0b] text-white flex items-center justify-center">
            <Gift className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#b45309]">Programma amici</div>
            <h3 className="font-extrabold text-[#0a1f55]">Invita e guadagna €{ref?.importo_per_referral || 10}</h3>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
        ) : ref ? (
          <>
            <p className="text-[12.5px] text-slate-700 mb-3">
              Per ogni amico che si iscrive con il tuo codice ricevi un <strong>buono da €{ref.importo_per_referral}</strong> utilizzabile al prossimo acquisto (validità 1 anno).
            </p>

            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="rounded-lg bg-white border p-2">
                <div className="text-[9.5px] uppercase tracking-wider font-extrabold text-slate-500">Referral</div>
                <div className="font-extrabold text-[#0a1f55] text-lg">{ref.referral_totali || 0}</div>
              </div>
              <div className="rounded-lg bg-white border p-2">
                <div className="text-[9.5px] uppercase tracking-wider font-extrabold text-slate-500">Buoni</div>
                <div className="font-extrabold text-emerald-700 text-lg">€ {ref.buono_disponibile?.toFixed(0) || 0}</div>
              </div>
              <div className="rounded-lg bg-white border p-2">
                <div className="text-[9.5px] uppercase tracking-wider font-extrabold text-slate-500">Codice</div>
                <div className="font-extrabold text-[#f59e0b] text-[13px] font-mono">{ref.ref_code}</div>
              </div>
            </div>

            <div className="rounded-lg bg-white border-2 border-dashed border-[#f59e0b]/60 p-3 mb-3">
              <div className="text-[11px] uppercase tracking-wider font-extrabold text-slate-500 mb-1">Il tuo link</div>
              <div className="font-mono text-[11.5px] text-[#0a1f55] break-all">{ref.ref_link}</div>
            </div>

            <div className="flex gap-2">
              <button onClick={copyLink} data-testid="copy-ref-link"
                className="flex-1 h-9 rounded-lg bg-[#0a1f55] hover:bg-[#102a6b] text-white font-bold text-[12px] inline-flex items-center justify-center gap-1.5">
                {copied ? <><CheckCircle2 className="h-3.5 w-3.5" /> Copiato</> : <><Copy className="h-3.5 w-3.5" /> Copia</>}
              </button>
              <button onClick={shareWhats} data-testid="share-whatsapp"
                className="flex-1 h-9 rounded-lg bg-[#25D366] hover:bg-[#1eb257] text-white font-bold text-[12px] inline-flex items-center justify-center gap-1.5">
                <Share2 className="h-3.5 w-3.5" /> WhatsApp
              </button>
            </div>
          </>
        ) : (
          <div className="text-red-500 text-[13px]">Errore caricamento referral</div>
        )}
      </div>
    </div>
  );
}
